// src/pages/MentionsLegales.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axiosInstance from "../axiosInstance";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";

/* ------------------------------ Quill ------------------------------ */
const SizeStyle = Quill.import("attributors/style/size");
SizeStyle.whitelist = ["12px", "14px", "16px", "18px", "24px", "32px"];
Quill.register(SizeStyle, true);
const SIZE_OPTIONS = ["12px", "14px", "16px", "18px", "24px", "32px"];

/* ------------------------------ Helpers ---------------------------- */
const isProbablyHtml = (s = "") => /<\/?[a-z][\s\S]*>/i.test(s);
const textToHtml = (txt = "") => {
  const esc = (t) =>
    t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const blocks = String(txt || "")
    .trim()
    .split(/\n{2,}/)
    .map((p) => `<p>${esc(p).replace(/\n/g, "<br/>")}</p>`);
  return blocks.join("") || "<p></p>";
};
const ensureHtml = (val = "") => (isProbablyHtml(val) ? val : textToHtml(val));
const htmlToText = (html = "") => {
  let s = String(html || "");
  s = s.replace(/<\s*br\s*\/?>/gi, "\n");
  s = s.replace(/<\/p>\s*<p>/gi, "\n\n");
  s = s.replace(/<\/?p[^>]*>/gi, "");
  s = s.replace(/<[^>]+>/g, "");
  s = s
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  return s.trim();
};

/* ---------------- TVA block (auto) ---------------- */
const RE_TVA = /(tva|293\s*B|hors\s*taxes|taux\s+en\s+vigueur)/i;
const buildTVALine = (isAuto) =>
  isAuto
    ? "TVA non applicable, article 293 B du Code général des impôts."
    : "Les prix sont exprimés en euros hors taxes, la TVA sera appliquée au taux en vigueur.";

function upsertTVABlockText(text = "", isAuto) {
  const t = String(text || "");
  const parts = t.split(/\n{2,}/);
  const line = buildTVALine(isAuto);
  if (parts.length === 0 || !parts[0].trim()) return line;
  if (RE_TVA.test(parts[0])) {
    parts[0] = line;
    return parts.join("\n\n");
  }
  return [line, t.trim()].filter(Boolean).join("\n\n");
}
function upsertTVABlockHtml(html = "", isAuto) {
  const h = String(html || "").trim();
  const pTVA = `<p>${buildTVALine(isAuto)}</p>`;
  if (!h) return pTVA;
  const m = h.match(/<p[^>]*>[\s\S]*?<\/p>/i);
  if (!m) return pTVA + h;
  if (RE_TVA.test(m[0])) return h.replace(m[0], pTVA);
  return pTVA + h;
}

/* ---------------- Numérotation helpers ---------------- */
const seqWidthFromFormat = (fmt) => {
  const m = fmt.match(/\{SEQ(\d+)\}/i);
  return m ? parseInt(m[1], 10) : 4;
};
const padSeq = (seq, width) =>
  String(Math.max(0, Number(seq || 0))).padStart(width, "0");
const formatNumber = (fmt, seq, date = new Date()) => {
  const yyyy = String(date.getFullYear());
  const yy = yyyy.slice(-2);
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const width = seqWidthFromFormat(fmt);
  return fmt
    .replace(/\{YYYY\}/g, yyyy)
    .replace(/\{YY\}/g, yy)
    .replace(/\{MM\}/g, mm)
    .replace(/\{DD\}/g, dd)
    .replace(/\{SEQ\d+\}/gi, padSeq(seq, width));
};

/* -------------------- Valeurs par défaut -------------------- */
const texteDevis =
  "Les tarifs indiqués sont établis sur la base des informations disponibles à la date du devis et pourront être ajustés en cas de modification des prestations ou du coût des matériaux.";
const texteTVAAutoEntrepreneur =
  "TVA non applicable, article 293 B du Code général des impôts.";
const texteTVAAutres =
  "Les prix sont exprimés en euros hors taxes, la TVA sera appliquée au taux en vigueur.";
const texteFacturation = `Tout retard de paiement d’une facture, qu’il soit partiel ou total, entraînera l’application de pénalités calculées à un taux équivalent à 1,5 fois le taux d’intérêt légal en vigueur. Ces pénalités seront appliquées de plein droit, et ce, dès le premier jour suivant la date d’échéance indiquée sur la facture.

Conformément à la législation en vigueur, une indemnité forfaitaire de 40 € pour frais de recouvrement pourra également être exigée.`;

/* ---------------- Moyens de paiement ---------------- */
const DEFAULT_MOYENS_PAIEMENT = {
  virement: true,
  cheque: true,
  especes: true,
  carteBancaire: false,
  paiementEnLigne: false,
};

function buildMoyensPaiementTexte(moyens = DEFAULT_MOYENS_PAIEMENT) {
  const parts = [
    moyens.virement && "virement bancaire",
    moyens.cheque && "chèque",
    moyens.especes && "espèces",
    moyens.carteBancaire && "carte bancaire",
    moyens.paiementEnLigne && "paiement en ligne",
  ].filter(Boolean);

  if (!parts.length) return "";

  if (parts.length === 1) {
    return `Moyen de paiement accepté : ${parts[0]}.`;
  }

  const last = parts.pop();
  return `Moyens de paiement acceptés : ${parts.join(", ")} et ${last}.`;
}

/* =============================== Page =============================== */
export default function MentionsLegales({
  onDirtyChange = () => {},
  setSaveHandler = () => {},
}) {
  /* Styles locaux (par défaut 12px dans les éditeurs) */
  const styles = `
    .card{border-radius:12px;border:1px solid #e5e7eb;background:#fff}
    .card-hd{display:flex;align-items:center;justify-content:space-between;padding:12px 16px;border-bottom:1px solid #eef1f4}
    .card-tt{font-weight:700;color:#0f172a}
    .card-bd{padding:16px}
    .quill12 .ql-editor{font-size:12px}
    .ql-container{max-height:none;overflow:visible}
  `;

  const [mentions, setMentions] = useState({
    formeJuridique: "",
    validiteDevis: "",
    delaiFacture: "",
    mentionsSupplementaires: "",
    mentionsFacturation: "",
    devisFormat: "DEV-{YYYY}-{SEQ4}",
    devisNextSeq: 1,
    devisResetAnnuel: true,
    factureFormat: "FAC-{YYYY}-{SEQ4}",
    factureNextSeq: 1,
    factureResetAnnuel: true,
    tauxTVA: 20,
    moyensPaiement: DEFAULT_MOYENS_PAIEMENT,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetMentions, setResetMentions] = useState(false);
  const [editDevisWysiwyg, setEditDevisWysiwyg] = useState(false);
  const [editFactureWysiwyg, setEditFactureWysiwyg] = useState(false);

  /* Toolbars : on garde le contrôle de taille dans CHAQUE bloc */
  const quillFormats = [
    "size",
    "bold",
    "italic",
    "underline",
    "align",
    "list",
    "bullet",
    "link",
    "clean",
  ];
  const quillModulesDevis = useMemo(
    () => ({ toolbar: { container: "#toolbar-mentions-devis" } }),
    []
  );
  const quillModulesFacture = useMemo(
    () => ({ toolbar: { container: "#toolbar-mentions-facture" } }),
    []
  );

  /* ---------- Dirty tracking ---------- */
  const initialSnapRef = useRef(null);
  const makeSnap = useCallback(
    () =>
      JSON.stringify({
        formeJuridique: mentions.formeJuridique || "",
        validiteDevis: mentions.validiteDevis || "",
        delaiFacture: mentions.delaiFacture || "",
        mentionsSupplementairesHtml: ensureHtml(
          mentions.mentionsSupplementaires ||
            (mentions.formeJuridique === "Auto-entrepreneur"
              ? `${texteTVAAutoEntrepreneur}\n\n${texteDevis}`
              : `${texteTVAAutres}\n\n${texteDevis}`)
        ),
        mentionsFacturationHtml: ensureHtml(
          mentions.mentionsFacturation ||
            `${
              mentions.formeJuridique === "Auto-entrepreneur"
                ? texteTVAAutoEntrepreneur
                : texteTVAAutres
            }\n\n${texteFacturation}`
        ),
        devisFormat: mentions.devisFormat || "",
        devisNextSeq: Number(mentions.devisNextSeq || 0),
        devisResetAnnuel: !!mentions.devisResetAnnuel,
        factureFormat: mentions.factureFormat || "",
        factureNextSeq: Number(mentions.factureNextSeq || 0),
        factureResetAnnuel: !!mentions.factureResetAnnuel,
        tauxTVA: Number(mentions.tauxTVA || 0),
        moyensPaiement: mentions.moyensPaiement || DEFAULT_MOYENS_PAIEMENT,
      }),
    [mentions]
  );

  const isDirty = useMemo(() => {
    if (!initialSnapRef.current) return false;
    return makeSnap() !== initialSnapRef.current;
  }, [makeSnap]);

  useEffect(() => {
    onDirtyChange(!!isDirty);
  }, [isDirty, onDirtyChange]);

  /* ------------------------- Fetch init ------------------------- */
  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get("/mentionslegales");
        const d = res.data || {};

        const loadedMoyensPaiement = (() => {
          if (d.moyens_paiement) {
            try {
              const parsed = JSON.parse(d.moyens_paiement);
              return { ...DEFAULT_MOYENS_PAIEMENT, ...parsed };
            } catch {
              return DEFAULT_MOYENS_PAIEMENT;
            }
          }
          return DEFAULT_MOYENS_PAIEMENT;
        })();

        setMentions((prev) => ({
          ...prev,
          formeJuridique: d.forme_juridique || "",
          validiteDevis: d.validite_devis || "",
          delaiFacture: d.delai_facture || "",
          mentionsSupplementaires: d.mentions_supplementaires || "",
          mentionsFacturation: d.mentions_facturation || "",
          devisFormat: d.devis_format || prev.devisFormat,
          devisNextSeq: Number(d.devis_next_seq ?? prev.devisNextSeq),
          devisResetAnnuel: Boolean(
            d.devis_reset_annuel ?? prev.devisResetAnnuel
          ),
          factureFormat: d.facture_format || prev.factureFormat,
          factureNextSeq: Number(d.facture_next_seq ?? prev.factureNextSeq),
          factureResetAnnuel: Boolean(
            d.facture_reset_annuel ?? prev.factureResetAnnuel
          ),
          tauxTVA: Number(d.tva_default_rate ?? prev.tauxTVA),
          moyensPaiement: loadedMoyensPaiement,
        }));

        const merged = {
          formeJuridique: d.forme_juridique || "",
          validiteDevis: d.validite_devis || "",
          delaiFacture: d.delai_facture || "",
          mentionsSupplementaires: d.mentions_supplementaires || "",
          mentionsFacturation: d.mentions_facturation || "",
          devisFormat: d.devis_format || "DEV-{YYYY}-{SEQ4}",
          devisNextSeq: Number(d.devis_next_seq ?? 1),
          devisResetAnnuel: Boolean(d.devis_reset_annuel ?? true),
          factureFormat: d.facture_format || "FAC-{YYYY}-{SEQ4}",
          factureNextSeq: Number(d.facture_next_seq ?? 1),
          factureResetAnnuel: Boolean(d.facture_reset_annuel ?? true),
          tauxTVA: Number(d.tva_default_rate ?? 20),
          moyensPaiement: loadedMoyensPaiement,
        };
        initialSnapRef.current = JSON.stringify({
          formeJuridique: merged.formeJuridique,
          validiteDevis: merged.validiteDevis,
          delaiFacture: merged.delaiFacture,
          mentionsSupplementairesHtml: ensureHtml(
            merged.mentionsSupplementaires ||
              (merged.formeJuridique === "Auto-entrepreneur"
                ? `${texteTVAAutoEntrepreneur}\n\n${texteDevis}`
                : `${texteTVAAutres}\n\n${texteDevis}`)
          ),
          mentionsFacturationHtml: ensureHtml(
            merged.mentionsFacturation ||
              `${
                merged.formeJuridique === "Auto-entrepreneur"
                  ? texteTVAAutoEntrepreneur
                  : texteTVAAutres
              }\n\n${texteFacturation}`
          ),
          devisFormat: merged.devisFormat,
          devisNextSeq: merged.devisNextSeq,
          devisResetAnnuel: merged.devisResetAnnuel,
          factureFormat: merged.factureFormat,
          factureNextSeq: merged.factureNextSeq,
          factureResetAnnuel: merged.factureResetAnnuel,
          tauxTVA: merged.tauxTVA,
          moyensPaiement: merged.moyensPaiement,
        });
        onDirtyChange(false);
      } catch (e) {
        console.error("Erreur chargement mentions légales :", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [onDirtyChange]);

  /* ------------------------------ Save ------------------------------ */
  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      let next = { ...mentions };

      if (resetMentions) {
        if (
          !window.confirm(
            "Réinitialiser les mentions légales aux textes par défaut ?"
          )
        ) {
          setSaving(false);
          return;
        }
        const blocTVA =
          next.formeJuridique === "Auto-entrepreneur"
            ? texteTVAAutoEntrepreneur
            : texteTVAAutres;
        next.mentionsSupplementaires = `${blocTVA}\n\n${texteDevis}`;
        next.mentionsFacturation = `${blocTVA}\n\n${texteFacturation}`;
      }

      const normalizeForStore = (v) => ensureHtml(v || "");

      const payload = {
        forme_juridique: next.formeJuridique || "",
        validite_devis: next.validiteDevis || "",
        delai_facture: next.delaiFacture || "",
        mentions_supplementaires: normalizeForStore(
          next.mentionsSupplementaires
        ),
        mentions_facturation: normalizeForStore(next.mentionsFacturation),
        devis_format: next.devisFormat,
        devis_next_seq: Number(next.devisNextSeq),
        devis_reset_annuel: Boolean(next.devisResetAnnuel),
        facture_format: next.factureFormat,
        facture_next_seq: Number(next.factureNextSeq),
        facture_reset_annuel: Boolean(next.factureResetAnnuel),
        tva_default_rate: Number(next.tauxTVA),
        moyens_paiement: JSON.stringify(
          next.moyensPaiement || DEFAULT_MOYENS_PAIEMENT
        ),
      };

      await axiosInstance.post("/mentionslegales", payload);
      setMentions(next);
      setResetMentions(false);

      initialSnapRef.current = makeSnap();
      onDirtyChange(false);

      alert("✅ Mentions légales / numérotation / TVA / moyens de paiement enregistrés !");
    } catch (e) {
      console.error("Erreur enregistrement mentions légales :", e);
      alert("❌ Erreur lors de l'enregistrement !");
    } finally {
      setSaving(false);
    }
  }, [mentions, resetMentions, makeSnap, onDirtyChange]);

  useEffect(() => {
    setSaveHandler(() => handleSave);
    return () => setSaveHandler(null);
  }, [setSaveHandler, handleSave]);

  /* ------------------------- listes ------------------------- */
  const formesJuridiques = [
    "Auto-entrepreneur",
    "EI (Entreprise Individuelle)",
    "EIRL",
    "EURL",
    "SARL",
    "SAS",
    "SASU",
    "SA",
    "SNC",
    "SCOP",
    "Association loi 1901",
    "SCA",
    "SCM",
    "SCI",
    "SCIC",
    "Autre",
  ];
  const validites = [
    "5 jours",
    "10 jours",
    "15 jours",
    "30 jours",
    "2 mois",
    "3 mois",
    "6 mois",
  ];
  const delaisFacture = [
    "À réception",
    "15 jours",
    "30 jours",
    "45 jours",
    "60 jours",
  ];

  const handleFormeJuridiqueChange = (e) => {
    const selected = e.target.value;
    const isAuto = selected === "Auto-entrepreneur";
    const tauxAuto = isAuto ? 0 : 20;

    setMentions((prev) => {
      const suppIsHtml = isProbablyHtml(prev.mentionsSupplementaires);
      const factIsHtml = isProbablyHtml(prev.mentionsFacturation);

      const newSupp = suppIsHtml
        ? upsertTVABlockHtml(prev.mentionsSupplementaires, isAuto)
        : upsertTVABlockText(
            prev.mentionsSupplementaires ||
              `${buildTVALine(isAuto)}\n\n${texteDevis}`,
            isAuto
          );

      const newFact = factIsHtml
        ? upsertTVABlockHtml(prev.mentionsFacturation, isAuto)
        : upsertTVABlockText(
            prev.mentionsFacturation ||
              `${buildTVALine(isAuto)}\n\n${texteFacturation}`,
            isAuto
          );

      return {
        ...prev,
        formeJuridique: selected,
        tauxTVA: tauxAuto,
        mentionsSupplementaires: newSupp,
        mentionsFacturation: newFact,
      };
    });
  };

  /* -------------------------- Aperçus numérotation ------------------------- */
  const previewDevis = useMemo(() => {
    try {
      return formatNumber(mentions.devisFormat, mentions.devisNextSeq);
    } catch {
      return "Format invalide";
    }
  }, [mentions.devisFormat, mentions.devisNextSeq]);

  const previewFacture = useMemo(() => {
    try {
      return formatNumber(mentions.factureFormat, mentions.factureNextSeq);
    } catch {
      return "Format invalide";
    }
  }, [mentions.factureFormat, mentions.factureNextSeq]);

  if (loading) return <p>Chargement des mentions légales…</p>;

  /* -------------------------------- UI -------------------------------- */
  return (
    <div className="w-full">
      <style>{styles}</style>

      <div className="mx-auto w-full max-w-[1200px] px-3 sm:px-4 lg:px-6 space-y-8">
        {/* Titre unique */}
        <h1 className="text-2xl font-bold text-slate-800 text-center">
          Mentions légales
        </h1>

        {/* Paramètres généraux */}
        <section className="card">
          <div className="card-hd">
            <div className="card-tt">Paramètres généraux</div>
          </div>
          <div className="card-bd">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-800 mb-1">
                  Forme juridique
                </label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={mentions.formeJuridique}
                  onChange={handleFormeJuridiqueChange}
                >
                  <option value="">— Sélectionner —</option>
                  {formesJuridiques.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-800 mb-1">
                  Validité des devis
                </label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={mentions.validiteDevis}
                  onChange={(e) =>
                    setMentions({
                      ...mentions,
                      validiteDevis: e.target.value,
                    })
                  }
                >
                  <option value="">— Choisir —</option>
                  {validites.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-800 mb-1">
                  Délai de règlement
                </label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={mentions.delaiFacture}
                  onChange={(e) =>
                    setMentions({
                      ...mentions,
                      delaiFacture: e.target.value,
                    })
                  }
                >
                  <option value="">— Choisir —</option>
                  {delaisFacture.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-800 mb-1">
                  TVA par défaut (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-full border rounded px-3 py-2"
                  value={mentions.tauxTVA}
                  onChange={(e) =>
                    setMentions({ ...mentions, tauxTVA: e.target.value })
                  }
                  placeholder="20"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Mentions DEVIS (bloc 1) */}
        <section className="card">
          <div className="card-hd">
            <div className="card-tt">Mentions légales du devis</div>
            {!editDevisWysiwyg ? (
              <button
                type="button"
                className="h-9 px-3 rounded-full text-[13px] border bg-white text-slate-700 hover:bg-slate-50"
                onClick={() => setEditDevisWysiwyg(true)}
              >
                Éditer librement
              </button>
            ) : (
              <button
                type="button"
                className="h-9 px-3 rounded-full text-[13px] border bg-white text-slate-700 hover:bg-slate-50"
                onClick={() => {
                  setEditDevisWysiwyg(false);
                  setMentions((prev) => ({
                    ...prev,
                    mentionsSupplementaires: htmlToText(
                      prev.mentionsSupplementaires
                    ),
                  }));
                }}
              >
                Revenir au texte
              </button>
            )}
          </div>
          <div className="card-bd">
            {!editDevisWysiwyg ? (
              <textarea
                className="w-full border rounded px-3 py-2 h-48"
                value={
                  isProbablyHtml(mentions.mentionsSupplementaires)
                    ? htmlToText(mentions.mentionsSupplementaires)
                    : mentions.mentionsSupplementaires ||
                      (mentions.formeJuridique === "Auto-entrepreneur"
                        ? `${texteTVAAutoEntrepreneur}\n\n${texteDevis}`
                        : `${texteTVAAutres}\n\n${texteDevis}`)
                }
                onChange={(e) =>
                  setMentions({
                    ...mentions,
                    mentionsSupplementaires: e.target.value,
                  })
                }
              />
            ) : (
              <>
                <div
                  id="toolbar-mentions-devis"
                  className="ql-toolbar ql-snow rounded-t-md mb-1"
                >
                  <select className="ql-size" defaultValue="">
                    <option value="">Défaut</option>
                    {SIZE_OPTIONS.map((v) => (
                      <option key={v} value={v}>
                        {parseInt(v, 10)}
                      </option>
                    ))}
                  </select>
                  <button className="ql-bold" />
                  <button className="ql-italic" />
                  <button className="ql-underline" />
                  <select className="ql-align" />
                  <button className="ql-list" value="ordered" />
                  <button className="ql-list" value="bullet" />
                  <button className="ql-link" />
                  <button className="ql-clean" />
                </div>
                <ReactQuill
                  theme="snow"
                  value={ensureHtml(
                    mentions.mentionsSupplementaires ||
                      (mentions.formeJuridique === "Auto-entrepreneur"
                        ? `${texteTVAAutoEntrepreneur}\n\n${texteDevis}`
                        : `${texteTVAAutres}\n\n${texteDevis}`)
                  )}
                  onChange={(html) =>
                    setMentions({
                      ...mentions,
                      mentionsSupplementaires: html,
                    })
                  }
                  modules={quillModulesDevis}
                  formats={quillFormats}
                  className="quill12 rounded-b-md bg-white"
                />
              </>
            )}
            <p className="text-[12px] text-slate-500 mt-2">
              Le premier paragraphe contient automatiquement la ligne TVA selon
              la forme juridique.
            </p>
          </div>
        </section>

        {/* Mentions FACTURATION (bloc 2) */}
        <section className="card">
          <div className="card-hd">
            <div className="card-tt">Mentions légales de facturation</div>
            {!editFactureWysiwyg ? (
              <button
                type="button"
                className="h-9 px-3 rounded-full text-[13px] border bg-white text-slate-700 hover:bg-slate-50"
                onClick={() => setEditFactureWysiwyg(true)}
              >
                Éditer librement
              </button>
            ) : (
              <button
                type="button"
                className="h-9 px-3 rounded-full text-[13px] border bg-white text-slate-700 hover:bg-slate-50"
                onClick={() => {
                  setEditFactureWysiwyg(false);
                  setMentions((prev) => ({
                    ...prev,
                    mentionsFacturation: htmlToText(prev.mentionsFacturation),
                  }));
                }}
              >
                Revenir au texte
              </button>
            )}
          </div>
          <div className="card-bd">
            {!editFactureWysiwyg ? (
              <textarea
                className="w-full border rounded px-3 py-2 h-48"
                value={
                  isProbablyHtml(mentions.mentionsFacturation)
                    ? htmlToText(mentions.mentionsFacturation)
                    : mentions.mentionsFacturation ||
                      `${
                        mentions.formeJuridique === "Auto-entrepreneur"
                          ? texteTVAAutoEntrepreneur
                          : texteTVAAutres
                      }\n\n${texteFacturation}`
                }
                onChange={(e) =>
                  setMentions({
                    ...mentions,
                    mentionsFacturation: e.target.value,
                  })
                }
              />
            ) : (
              <>
                <div
                  id="toolbar-mentions-facture"
                  className="ql-toolbar ql-snow rounded-t-md mb-1"
                >
                  <select className="ql-size" defaultValue="">
                    <option value="">Défaut</option>
                    {SIZE_OPTIONS.map((v) => (
                      <option key={v} value={v}>
                        {parseInt(v, 10)}
                      </option>
                    ))}
                  </select>
                  <button className="ql-bold" />
                  <button className="ql-italic" />
                  <button className="ql-underline" />
                  <select className="ql-align" />
                  <button className="ql-list" value="ordered" />
                  <button className="ql-list" value="bullet" />
                  <button className="ql-link" />
                  <button className="ql-clean" />
                </div>
                <ReactQuill
                  theme="snow"
                  value={ensureHtml(
                    mentions.mentionsFacturation ||
                      `${
                        mentions.formeJuridique === "Auto-entrepreneur"
                          ? texteTVAAutoEntrepreneur
                          : texteTVAAutres
                      }\n\n${texteFacturation}`
                  )}
                  onChange={(html) =>
                    setMentions({
                      ...mentions,
                      mentionsFacturation: html,
                    })
                  }
                  modules={quillModulesFacture}
                  formats={quillFormats}
                  className="quill12 rounded-b-md bg-white"
                />
              </>
            )}
            <p className="text-[12px] text-slate-500 mt-2">
              Le premier paragraphe contient automatiquement la ligne TVA selon
              la forme juridique.
            </p>
          </div>
        </section>

        {/* Moyens de paiement acceptés */}
        <section className="card">
          <div className="card-hd">
            <div className="card-tt">Moyens de paiement acceptés</div>
          </div>
          <div className="card-bd">
            <div className="flex flex-wrap gap-4">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={mentions.moyensPaiement.virement}
                  onChange={(e) =>
                    setMentions((prev) => ({
                      ...prev,
                      moyensPaiement: {
                        ...prev.moyensPaiement,
                        virement: e.target.checked,
                      },
                    }))
                  }
                />
                <span>Virement bancaire</span>
              </label>

              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={mentions.moyensPaiement.cheque}
                  onChange={(e) =>
                    setMentions((prev) => ({
                      ...prev,
                      moyensPaiement: {
                        ...prev.moyensPaiement,
                        cheque: e.target.checked,
                      },
                    }))
                  }
                />
                <span>Chèque</span>
              </label>

              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={mentions.moyensPaiement.especes}
                  onChange={(e) =>
                    setMentions((prev) => ({
                      ...prev,
                      moyensPaiement: {
                        ...prev.moyensPaiement,
                        especes: e.target.checked,
                      },
                    }))
                  }
                />
                <span>Espèces</span>
              </label>

              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={mentions.moyensPaiement.carteBancaire}
                  onChange={(e) =>
                    setMentions((prev) => ({
                      ...prev,
                      moyensPaiement: {
                        ...prev.moyensPaiement,
                        carteBancaire: e.target.checked,
                      },
                    }))
                  }
                />
                <span>Carte bancaire</span>
              </label>

              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={mentions.moyensPaiement.paiementEnLigne}
                  onChange={(e) =>
                    setMentions((prev) => ({
                      ...prev,
                      moyensPaiement: {
                        ...prev.moyensPaiement,
                        paiementEnLigne: e.target.checked,
                      },
                    }))
                  }
                />
                <span>Paiement en ligne</span>
              </label>
            </div>

            <p className="mt-3 text-sm text-slate-600">
              {buildMoyensPaiementTexte(mentions.moyensPaiement) ||
                "Sélectionnez au moins un moyen de paiement pour générer la phrase."}
            </p>
          </div>
        </section>

        {/* Numérotation Devis / Factures */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <section className="card">
            <div className="card-hd">
              <div className="card-tt">Numérotation des devis</div>
              <span className="text-[12px] text-slate-500">
                {"{YYYY},{YY},{MM},{DD},{SEQn}"}
              </span>
            </div>
            <div className="card-bd">
              <label className="block text-sm font-medium text-slate-800 mb-1">
                Format
              </label>
              <select
                className="w-full border rounded px-3 py-2 mb-3"
                value={mentions.devisFormat}
                onChange={(e) =>
                  setMentions({ ...mentions, devisFormat: e.target.value })
                }
              >
                {[
                  { label: "DEV-YYYY-0001", value: "DEV-{YYYY}-{SEQ4}" },
                  { label: "DEV-YYMM-001", value: "DEV-{YY}{MM}-{SEQ3}" },
                  {
                    label: "DEV-YYYYMMDD-01",
                    value: "DEV-{YYYY}{MM}{DD}-{SEQ2}",
                  },
                  { label: "DEV-00001", value: "DEV-{SEQ5}" },
                  { label: "DEV-YY-0001", value: "DEV-{YY}-{SEQ4}" },
                ].map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-800 mb-1">
                    Prochain numéro
                  </label>
                  <input
                    type="number"
                    min={0}
                    className="w-full border rounded px-3 py-2"
                    value={mentions.devisNextSeq}
                    onChange={(e) =>
                      setMentions({
                        ...mentions,
                        devisNextSeq: Number(e.target.value || 0),
                      })
                    }
                  />
                  <p className="text-[12px] text-slate-500 mt-1">
                    Exemple 150 → suffixe :{" "}
                    <em>
                      {padSeq(150, seqWidthFromFormat(mentions.devisFormat))}
                    </em>
                  </p>
                </div>
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={mentions.devisResetAnnuel}
                      onChange={(e) =>
                        setMentions({
                          ...mentions,
                          devisResetAnnuel: e.target.checked,
                        })
                      }
                    />
                    <span className="text-sm text-slate-800">
                      Reset annuel
                    </span>
                  </label>
                </div>
              </div>

              <div className="mt-3 text-sm">
                <span className="text-slate-600">Aperçu : </span>
                <span className="font-semibold">{previewDevis}</span>
              </div>
            </div>
          </section>

          <section className="card">
            <div className="card-hd">
              <div className="card-tt">Numérotation des factures</div>
              <span className="text-[12px] text-slate-500">
                {"{YYYY},{YY},{MM},{DD},{SEQn}"}
              </span>
            </div>
            <div className="card-bd">
              <label className="block text-sm font-medium text-slate-800 mb-1">
                Format
              </label>
              <select
                className="w-full border rounded px-3 py-2 mb-3"
                value={mentions.factureFormat}
                onChange={(e) =>
                  setMentions({ ...mentions, factureFormat: e.target.value })
                }
              >
                {[
                  { label: "FAC-YYYY-0001", value: "FAC-{YYYY}-{SEQ4}" },
                  { label: "FAC-YYMM-001", value: "FAC-{YY}{MM}-{SEQ3}" },
                  {
                    label: "FAC-YYYYMMDD-01",
                    value: "FAC-{YYYY}{MM}{DD}-{SEQ2}",
                  },
                  { label: "FAC-00001", value: "FAC-{SEQ5}" },
                  { label: "FAC-YY-0001", value: "FAC-{YY}-{SEQ4}" },
                ].map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-800 mb-1">
                    Prochain numéro
                  </label>
                  <input
                    type="number"
                    min={0}
                    className="w-full border rounded px-3 py-2"
                    value={mentions.factureNextSeq}
                    onChange={(e) =>
                      setMentions({
                        ...mentions,
                        factureNextSeq: Number(e.target.value || 0),
                      })
                    }
                  />
                  <p className="text-[12px] text-slate-500 mt-1">
                    Exemple 150 → suffixe :{" "}
                    <em>
                      {padSeq(150, seqWidthFromFormat(mentions.factureFormat))}
                    </em>
                  </p>
                </div>
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={mentions.factureResetAnnuel}
                      onChange={(e) =>
                        setMentions({
                          ...mentions,
                          factureResetAnnuel: e.target.checked,
                        })
                      }
                    />
                    <span className="text-sm text-slate-800">
                      Reset annuel
                    </span>
                  </label>
                </div>
              </div>

              <div className="mt-3 text-sm">
                <span className="text-slate-600">Aperçu : </span>
                <span className="font-semibold">{previewFacture}</span>
              </div>
            </div>
          </section>
        </div>

        {/* Actions globales */}
        <div className="card">
          <div className="card-bd">
            <div className="flex flex-wrap items-center gap-4">
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full shadow bg-sky-700 hover:bg-sky-600 disabled:opacity-60 text-white text-sm"
              >
                {saving
                  ? "Enregistrement…"
                  : "Enregistrer tous les paramètres"}
              </button>
              <label className="inline-flex items-center gap-2 text-sm select-none">
                <input
                  type="checkbox"
                  checked={resetMentions}
                  onChange={(e) => setResetMentions(e.target.checked)}
                />
                Réinitialiser les mentions légales lors de l’enregistrement
              </label>
              <span className="text-[12px] text-slate-500">
                (les textes seront régénérés avec la ligne TVA + modèles par
                défaut)
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
